import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useStore } from './stores';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { MainLayout } from './components/layout/MainLayout';
import ChatPage from './pages/main/ChatPage';
import HistoryPage from './pages/main/HistoryPage';
import ProfilePage from './pages/main/ProfilePage';
import { SettingsPage } from './pages/main/SettingsPage';
import GlobalKaTeX from './services/math/GlobalKaTeX';
// import { apiService } from '@ai-math-tutor/shared/services/api/ApiService';

import { ErrorBoundary } from './components/common/ErrorBoundary';
import { RoleProtectedRoute } from './components/auth/RoleProtectedRoute';
import { TeachLayout } from './pages/teach/TeachLayout';
import { StudentsPage } from './pages/teach/StudentsPage';
import { ClassesPage } from './pages/teach/ClassesPage';
import { LearningPage } from './pages/teach/LearningPage';
import { AbilityPage } from './pages/teach/AbilityPage';
import { LearningManagementPage } from './pages/teach/LearningManagementPage';
import { MenuDebugPage } from './pages/teach/MenuDebugPage';
import { StudentAssignmentsPage } from './pages/main/StudentAssignmentsPage';
import { HomeworkListPage } from './pages/teach/HomeworkListPage';
import { CreateHomeworkPage } from './pages/teach/CreateHomeworkPage';
import { HomeworkDetailPage } from './pages/teach/HomeworkDetailPage';
import { FamilyPage } from './pages/family/FamilyPage';
import { AddChildPage } from './pages/family/AddChildPage';
import { ParentLayout } from './pages/parent/ParentLayout';
import { apiService } from '@shared/services/api/ApiService';
import { socketService } from './services/SocketService';

/**
 * 主应用组件 - 处理路由和认证状态
 */
const App: React.FC = () => {
  const { user, setUser, setTokens, isAuthenticated, logout } = useStore();

  // KaTeX预加载 - 在应用启动时预加载，降低首次渲染延迟
  useEffect(() => {
    // 使用全局KaTeX管理器进行预加载
    GlobalKaTeX.preload();
  }, []);

  // 监听认证失败事件
  useEffect(() => {
    const handleAuthFailed = () => {
      console.log('[App] 认证失败，清除状态并跳转到登录页');
      // 清除本地状态
      logout();
      // 跳转到登录页
      window.location.href = '/login?reason=session_expired';
    };

    // 监听 apiService 的认证失败事件
    apiService.on('auth_failed', handleAuthFailed);

    // 监听 socketService 的 token 过期事件
    socketService.on('token_expired', handleAuthFailed);

    // 清理函数
    return () => {
      apiService.off('auth_failed', handleAuthFailed);
      socketService.off('token_expired', handleAuthFailed);
    };
  }, [logout]);

  // 应用启动时恢复认证状态
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const refreshToken = localStorage.getItem('auth_refresh_token');
    const userStr = localStorage.getItem('auth_user');

    // 验证token格式是否正确（JWT应该有3个部分，用.分隔）
    const isValidJWT = (token: string | null) => {
      if (!token || typeof token !== 'string' || token === 'undefined' || token === 'null' || token.trim() === '') {
        return false;
      }
      return token.split('.').length === 3;
    };

    if (token && refreshToken && userStr && isValidJWT(token)) {
      try {
        const userData = JSON.parse(userStr);

        setUser(userData);
        setTokens(token, refreshToken);
        // apiService.setToken(token);
      } catch (error) {
        console.error('Failed to restore auth state:', error);
        // 清除无效的存储数据
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_refresh_token');
        localStorage.removeItem('auth_user');
      }
    } else if (token || refreshToken || userStr) {
      // 如果存在部分数据但不完整或无效，清除所有数据
      console.warn('Invalid or incomplete auth data found, clearing...');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_refresh_token');
      localStorage.removeItem('auth_user');
    }
  }, [setUser, setTokens]);

  return (
    <ErrorBoundary>
      <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
          <Routes>
            {/* 公开路由 - 未认证用户可访问 */}
            <Route
              path="/login"
              element={isAuthenticated ? <Navigate to="/chat" replace /> : <LoginPage />}
            />
            <Route
              path="/register"
              element={isAuthenticated ? <Navigate to="/chat" replace /> : <RegisterPage />}
            />
            <Route
              path="/forgot-password"
              element={isAuthenticated ? <Navigate to="/chat" replace /> : <ForgotPasswordPage />}
            />

            {/* 受保护的路由 - 需要认证 */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <Routes>
                      <Route path="/" element={
                        <Navigate to={
                          user?.role === 'parent'
                            ? "/parent/family"
                            : (user?.role === 'teacher' || user?.role === 'super_admin')
                              ? "/teach/students"
                              : "/chat"
                        } replace />
                      } />
                      <Route path="/chat" element={<ChatPage />} />
                      <Route path="/history" element={<HistoryPage />} />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/settings" element={<SettingsPage />} />
                      <Route path="/assignments" element={<StudentAssignmentsPage />} />
                      <Route path="/family" element={<FamilyPage />} />
                      <Route path="/family/add-child" element={<AddChildPage />} />
                      <Route path="*" element={<Navigate to="/chat" replace />} />
                    </Routes>
                  </MainLayout>
                </ProtectedRoute>
              }
            />

            {/* 家长端路由 - 需要parent或super_admin角色 */}
            <Route
              path="/parent"
              element={
                <ProtectedRoute>
                  <RoleProtectedRoute allowedRoles={['parent', 'super_admin']} />
                </ProtectedRoute>
              }
            >
              <Route element={<ParentLayout />}>
                <Route index element={<Navigate to="/parent/family" replace />} />
                <Route path="family" element={<FamilyPage />} />
                <Route path="family/add-child" element={<AddChildPage />} />
                <Route path="exercises" element={<LearningManagementPage />} />
                <Route path="homeworks" element={<HomeworkListPage />} />
                <Route path="homeworks/create" element={<CreateHomeworkPage />} />
                <Route path="homeworks/:id" element={<HomeworkDetailPage />} />
              </Route>
            </Route>

            {/* 教师端路由 - 需要teacher或super_admin角色 */}
            <Route
              path="/teach"
              element={
                <ProtectedRoute>
                  <RoleProtectedRoute allowedRoles={['teacher', 'super_admin']} />
                </ProtectedRoute>
              }
            >
              <Route element={<TeachLayout />}>
                <Route index element={<Navigate to="/teach/students" replace />} />
                <Route path="students" element={<StudentsPage />} />
                <Route path="classes" element={<ClassesPage />} />
                <Route path="learning" element={<LearningPage />} />
                <Route path="learning-management" element={<LearningManagementPage />} />
                <Route path="homeworks" element={<HomeworkListPage />} />
                <Route path="homeworks/create" element={<CreateHomeworkPage />} />
                <Route path="homeworks/:id" element={<HomeworkDetailPage />} />
                <Route path="menu-debug" element={<MenuDebugPage />} />
                <Route path="ability" element={<AbilityPage />} />
              </Route>
            </Route>
          </Routes>
        </div>
      </Router>
    </ErrorBoundary>
  );
};

export default App;